import { useMemo } from 'react';
import {
  Chips,
  DataTable,
  EmptyState,
  Icon,
  Input,
  Panel,
  Stat,
  type Column,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { adminColumns } from './columns.tsx';
import type { AdminRow, MatrixPayload } from './types.ts';

/**
 * Tab 1 — the operator roster and the four compliance tiles.
 *
 * Split out of AccessControl.tsx unchanged in behaviour. The tab owns no state:
 * every value it needs is decided by the module, because a suspension moves the
 * tiles as well as the row and a tab that held its own copy would disagree with
 * the drawer that made the change.
 */

export interface RosterFigures {
  twoFactorRoles: Set<string>;
  privileged: AdminRow[];
  /** Money capability held, and no CONFIRMED factor — includes Pending setup. */
  unenrolled: AdminRow[];
  active: number;
  suspended: number;
  invited: number;
  rolesInUse: number;
}

export function Roster({
  admins,
  visible,
  matrix,
  figures,
  query,
  onQuery,
  roleFilter,
  onRoleFilter,
  loading,
  failed,
  matrixFailed,
  matrixLoading,
  onSelect,
}: {
  admins: AdminRow[];
  visible: AdminRow[];
  matrix: MatrixPayload | null;
  figures: RosterFigures;
  query: string;
  onQuery: (v: string) => void;
  roleFilter: string;
  onRoleFilter: (v: string) => void;
  loading: boolean;
  failed: boolean;
  matrixFailed: boolean;
  matrixLoading: boolean;
  onSelect: (a: AdminRow) => void;
}) {
  const columns = useMemo<Column<AdminRow>[]>(() => adminColumns(onSelect), [onSelect]);

  const roleOptions = useMemo(
    () => [
      { key: 'all', label: 'All', count: admins.length },
      ...(matrix?.roles ?? []).map((r) => ({
        key: r.name,
        label: r.name,
        count: admins.filter((a) => a.role === r.name).length,
      })),
    ],
    [admins, matrix],
  );

  /* The four tiles are arithmetic over BOTH the roster and the matrix —
     `privileged` needs the roles that carry a 2FA-mandatory capability — so
     either read failing makes all four figures nobody measured, not small ones. */
  const figuresFailed = failed || matrixFailed;
  const figuresBusy = (loading && admins.length === 0) || (matrixLoading && matrix === null);

  return (
    <>
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          Roles are additive permission sets: an admin holds exactly one role plus an optional
          per-brand scope, and an empty scope means every brand. 2FA is mandatory for any role
          holding funds.approve or promo.publish — the roster below marks anyone who has the
          capability without the factor, including anyone whose enrolment is only half done.
        </div>
      </div>

      {/* Withheld rather than zeroed. Every one of the four is arithmetic over
          the roster and the matrix, so an unread payload turns them into figures
          nobody measured — and "every money-capable admin is enrolled" is a
          security claim a lead would act on. */}
      {figuresFailed ? (
        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            The compliance figures are withheld because{' '}
            {failed && matrixFailed
              ? 'neither the roster nor the permission matrix could be read'
              : failed
                ? 'the roster could not be read'
                : 'the permission matrix could not be read'}
            . All four tiles are counted from those two lists, so drawing them now would put a
            zero where a measurement should be — including a clean bill of health on second
            factors that nothing on this page currently knows to be true.
          </div>
        </div>
      ) : figuresBusy ? (
        <div className="stat-grid">
          {Array.from({ length: 4 }, (_, i) => (
            <div className="stat" key={i}>
              <div className="skeleton" style={{ height: '11px', width: '60%' }}>
                &nbsp;
              </div>
              <div className="skeleton" style={{ height: '25px', width: '80%' }}>
                &nbsp;
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="stat-grid">
          <Stat
            label="Admin users"
            value={formatCount(admins.length)}
            delta={`${formatCount(figures.active)} active · ${formatCount(figures.suspended)} suspended · ${formatCount(figures.invited)} invited`}
          />
          <Stat
            label="Hold money capabilities"
            value={formatCount(figures.privileged.length)}
            delta="roles carrying funds.approve, funds.adjust or promo.publish"
          />
          <Stat
            label="Without a second factor"
            value={formatCount(figures.unenrolled.length)}
            deltaUp={figures.unenrolled.length > 0}
            delta={
              figures.unenrolled.length > 0
                ? /* Named, with the STATE each one is in: "started enrolling and
                     stopped" and "never started" are different problems and the
                     tile used to flatten both into None. */
                  figures.unenrolled
                    .map((a) => `${a.display_name} (${a.two_factor.toLowerCase()})`)
                    .join(', ')
                : 'every money-capable admin has a confirmed authenticator'
            }
          />
          <Stat
            label="Roles in use"
            value={`${formatCount(figures.rolesInUse)} of ${formatCount(matrix?.roles.length ?? 0)}`}
            delta="one role per admin, plus optional per-brand scope"
          />
        </div>
      )}

      {/* Both controls narrow a list. With no list they would narrow nothing
          while implying there is something to narrow, and their chip counts
          would be four more zeros nobody counted. */}
      {!failed && (
        <>
          <div className="row">
            <div className="search-field" style={{ width: '100%', maxWidth: '420px' }}>
              <Icon name="magnifying-glass" />
              <Input
                value={query}
                onChange={(e) => onQuery(e.target.value)}
                placeholder="Name, email or role"
                aria-label="Search admin users by name, email or role"
              />
            </div>
          </div>

          {/* Single-select, and it combines with the search box rather than
              replacing it — both narrow the same list. */}
          <Chips
            options={roleOptions}
            active={roleFilter}
            onChange={onRoleFilter}
            label="Filter admin users by role"
          />
        </>
      )}

      <Panel>
        {loading && admins.length === 0 ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Admin users with role, status, second factor, brand scope and last activity"
            columns={columns}
            rows={visible}
            rowKey={(a) => a.id}
            onRowClick={onSelect}
            empty={
              <EmptyState
                icon={failed ? 'warning-circle' : 'users-three'}
                title={failed ? 'The roster could not be read' : 'No admin matches'}
              >
                {/* The failed read is answered FIRST, because the two branches
                    below both describe a list that arrived. The read that failed
                    is GET /api/access/admins, and the sentence deliberately does
                    not name it: an operator cannot open a path, cannot quote it
                    to anybody who will act on it, and learns nothing from it that
                    "the roster could not be read" does not tell them better. */}
                {failed
                  ? 'The attempt to read the roster did not answer, so this table is empty because nothing was read — not because there are no admin users. Who holds which role is unchanged; it is simply unknown here until the read succeeds. Try again shortly.'
                  : query || roleFilter !== 'all'
                    ? `Nothing matches ${query ? `“${query}”` : 'this filter'}${roleFilter !== 'all' ? ` in ${roleFilter}` : ''}. Search covers name, email and role.`
                    : 'The roster is empty, which cannot happen while you are signed in — reload before trusting this.'}
              </EmptyState>
            }
          />
        )}
      </Panel>
    </>
  );
}
